import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

// ─── Config ───
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;
const GEMINI_KEY = process.env.REACT_APP_GEMINI_KEY;
const ODDS_KEY = process.env.REACT_APP_ODDS_KEY;
const GEMINI_BASE = "https://generativelanguage.googleapis.com/v1beta/models/";
const ODDS_BASE = "https://api.the-odds-api.com/v4";

// ─── Supabase ───
const supaFetch = async (path, opts = {}) => {
  const token = localStorage.getItem("sb_token") || SUPABASE_KEY;
  const res = await fetch(SUPABASE_URL + path, {
    ...opts,
    headers: { apikey: SUPABASE_KEY, Authorization: "Bearer " + token, "Content-Type": "application/json", Prefer: opts.prefer || "return=representation", ...opts.headers },
  });
  if (!res.ok) throw new Error(await res.text());
  const t = await res.text();
  return t ? JSON.parse(t) : null;
};
const supaAuth = async (action, body) => {
  const res = await fetch(SUPABASE_URL + "/auth/v1/" + action, {
    method: "POST", headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error_description || data.msg || data.error || "Auth error");
  return data;
};

// ─── Gemini AI ───
const MODELS = [
  "gemini-2.5-flash",
  "gemini-2.0-flash-lite",
  "gemini-2.5-flash-lite",
];
const callGemini = async (parts) => {
  let lastError = "";
  for (const model of MODELS) {
    try {
      const url = "https://generativelanguage.googleapis.com/v1beta/models/" + model + ":generateContent?key=" + GEMINI_KEY;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contents: [{ parts }], generationConfig: { temperature: 0.1, maxOutputTokens: 8192 } }),
      });
      if (!res.ok) { lastError = await res.text(); console.warn("Gemini " + model + " failed:", lastError); continue; }
      const data = await res.json();
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text || "";
      if (text) { console.log("Using model:", model); return text; }
    } catch (e) { lastError = e.message; }
  }
  throw new Error("All models failed: " + lastError);
};

const extractJSON = (text) => {
  let clean = text.replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();
  try { return JSON.parse(clean); } catch(e) {}
  const s = clean.indexOf("{"), e = clean.lastIndexOf("}");
  if (s !== -1 && e > s) try { return JSON.parse(clean.substring(s, e + 1)); } catch(x) {}
  throw new Error("JSON parse failed: " + clean.substring(0, 200));
};

const parseBetSlip = async (base64, mediaType) => {
  const text = await callGemini([
    { inlineData: { mimeType: mediaType, data: base64 } },
    { text: `You are a sports betting expert. Extract ALL information from this bet slip screenshot.
Respond ONLY with valid JSON, no markdown, no backticks, no extra text:
{"bets":[{"sport":"NBA|NFL|MLB|NHL|Soccer|NCAAF|NCAAB|Tennis|Golf|MMA|Other","league":"string","betType":"Moneyline|Spread|Over/Under|Player Prop|Other","teams":{"home":"Team","away":"Team"},"selection":"exact pick","odds":"+150 or -110","player":"name or null","statType":"stat or null","line":"number or null"}],"betType":"single|parlay|same_game_parlay|teaser|other","totalOdds":"odds","wager":"amount or null","potentialPayout":"amount or null","sportsbook":"DraftKings|FanDuel|ESPN BET|PrizePicks|BetMGM|Bookie"}
If you cannot identify the sportsbook from the screenshot, default to Bookie.` }
  ]);
  return extractJSON(text);
};

const analyzeDeep = async (parsedBet) => {
  const text = await callGemini([
    { text: `You are an elite sports betting analyst with access to current 2026 season data. Analyze this bet with extreme depth.

BET: ${JSON.stringify(parsedBet)}

For EACH leg research deeply:
MLB: Starting pitchers (ERA, WHIP, K/9, record, last 5), bullpen ERA, batting avg vs LHP/RHP, record last 10, H/A splits, H2H, weather.
NBA: Record last 10, H/A, pace, OFF/DEF rating, B2B, injuries, ATS, player props (last 10/20 game log, hit rate, matchup).
NFL/NCAAF: ATS record, OFF/DEF rankings, turnovers, red zone, injuries, weather, rivalry.
NHL: Goalie (GAA, SV%), form last 10, PP/PK%, H/A, B2B, H2H.
Soccer: Position, form last 5, GF/GA per game, H/A, H2H, absences, xG.
Player Props: Last 10/20 game stat log, hit rate over line, matchup data, minutes/usage, H/A splits.

Respond ONLY with valid JSON:
{"overallConfidence":0-100,"overallVerdict":"one sentence","legs":[{"selection":"pick","confidence":0-100,"keyStats":["stat1","stat2","stat3","stat4","stat5"],"recentForm":"detail","injuries":"info or None reported","historicalHitRate":"X/Y in last Z games","matchupAnalysis":"matchup detail","verdict":"Smart|Lean|Risky|Fade","reasoning":"3-4 sentences with real numbers"}],"edgeRating":"Positive EV|Neutral|Negative EV","recommendations":["tip1","tip2","tip3"]}` }
  ]);
  return extractJSON(text);
};

// ─── Odds API ───
const SPORT_MAP = { MLB: "baseball_mlb", NBA: "basketball_nba", NFL: "americanfootball_nfl", NHL: "icehockey_nhl", NCAAF: "americanfootball_ncaaf", NCAAB: "basketball_ncaab", Soccer: "soccer_epl", EPL: "soccer_epl", "La Liga": "soccer_spain_la_liga", MLS: "soccer_usa_mls" };

const fetchOddsComparison = async (parsedBet) => {
  const results = [];
  const seenSports = new Set();
  for (const bet of (parsedBet.bets || [])) {
    const sportKey = SPORT_MAP[bet.sport] || SPORT_MAP[bet.league] || null;
    if (!sportKey || seenSports.has(sportKey)) continue;
    seenSports.add(sportKey);
    try {
      const res = await fetch(ODDS_BASE + "/sports/" + sportKey + "/odds/?apiKey=" + ODDS_KEY + "&regions=us&markets=h2h,spreads,totals&oddsFormat=american");
      if (res.ok) { const data = await res.json(); results.push(...data); }
    } catch (e) { console.error("Odds fetch error:", e); }
  }
  return results;
};

// ─── Theme ───
const T = {
  bg: "#060b14", surface: "#0d1420", card: "#111d2e", cardHover: "#162438",
  accent: "#00e676", accentDim: "rgba(0,230,118,0.12)",
  warn: "#ffc107", warnDim: "rgba(255,193,7,0.12)",
  danger: "#ff5252", dangerDim: "rgba(255,82,82,0.12)",
  blue: "#448aff", blueDim: "rgba(68,138,255,0.12)",
  purple: "#b388ff", purpleDim: "rgba(179,136,255,0.12)",
  text: "#e4e8ee", dim: "#6b7a90", faint: "#2a3548",
  border: "#1a2a3e", glow: "0 0 20px rgba(0,230,118,0.15)",
};
const confColor = v => v >= 70 ? T.accent : v >= 50 ? T.warn : T.danger;
const confLabel = v => v >= 80 ? "STRONG" : v >= 65 ? "SOLID" : v >= 50 ? "LEAN" : v >= 35 ? "RISKY" : "FADE";

const btnBase = { border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontFamily: "inherit", transition: "all 0.2s", fontSize: 14 };
const btnPrimary = { ...btnBase, background: T.accent, color: T.bg, padding: "12px 24px" };
const btnSecondary = { ...btnBase, background: T.card, color: T.text, padding: "10px 20px", border: "1px solid " + T.border };
const inputStyle = { width: "100%", padding: "12px 16px", background: T.surface, border: "1px solid " + T.border, borderRadius: 10, color: T.text, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

const Icon = ({ d, size = 20, color = T.dim }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);
const Icons = {
  home: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10",
  upload: "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M17 8l-5-5-5 5 M12 3v12",
  dollar: "M12 1v22 M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  history: "M12 8v4l3 3 M3 12a9 9 0 1018 0 9 9 0 00-18 0",
  logout: "M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M16 17l5-5-5-5 M21 12H9",
};

// ─── Logo Component ───
const Logo = ({ size = 40 }) => <img src="/logo.jpg" alt="LayStat" style={{ width: size, height: size, borderRadius: size > 50 ? 16 : 10, objectFit: "cover" }} />;

// ─── Confidence Ring ───
function ConfRing({ value, size = 100 }) {
  const r = (size - 12) / 2, c = 2 * Math.PI * r, offset = c - (value / 100) * c, col = confColor(value);
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.faint} strokeWidth={6} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={6} strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease", filter: "drop-shadow(0 0 6px " + col + ")" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: size * 0.3, fontWeight: 800, color: col, fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
        <span style={{ fontSize: size * 0.1, color: T.dim, fontWeight: 700, letterSpacing: 1 }}>{confLabel(value)}</span>
      </div>
    </div>
  );
}

// ─── Auth ───
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const handleSubmit = async () => {
    setError(""); setLoading(true);
    try {
      if (mode === "signup") {
        const data = await supaAuth("signup", { email, password });
        if (data.user && !data.session) { setError("Check your email to confirm, then log in."); setMode("login"); }
        else if (data.access_token) { localStorage.setItem("sb_token", data.access_token); localStorage.setItem("sb_refresh", data.refresh_token); onAuth(data.user, data.access_token); }
      } else {
        const data = await supaAuth("token?grant_type=password", { email, password });
        localStorage.setItem("sb_token", data.access_token); localStorage.setItem("sb_refresh", data.refresh_token); onAuth(data.user, data.access_token);
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  };
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 440, animation: "fadeUp 0.5s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <Logo size={80} />
          <h1 style={{ fontSize: 36, fontWeight: 800, color: T.text, letterSpacing: -1, margin: "12px 0 0", fontFamily: "'Bebas Neue', 'Outfit', sans-serif" }}>LAYSTAT</h1>
          <p style={{ color: T.dim, fontSize: 13, marginTop: 2, fontWeight: 500, letterSpacing: 2 }}>AI POWERED BET ANALYSIS</p>
        </div>
        <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 16, padding: 24 }}>
          <div style={{ display: "flex", gap: 4, background: T.surface, borderRadius: 10, padding: 3, marginBottom: 20 }}>
            {["login", "signup"].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(""); }}
                style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", cursor: "pointer", background: mode === m ? T.accent : "transparent", color: mode === m ? T.bg : T.dim, fontWeight: 700, fontSize: 14, fontFamily: "inherit" }}>
                {m === "login" ? "Log In" : "Sign Up"}
              </button>
            ))}
          </div>
          {error && <div style={{ background: error.includes("Check") ? T.accentDim : T.dangerDim, border: "1px solid " + (error.includes("Check") ? T.accent : T.danger) + "44", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: error.includes("Check") ? T.accent : T.danger }}>{error}</div>}
          <label style={{ fontSize: 12, fontWeight: 600, color: T.dim }}>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" style={{ ...inputStyle, marginTop: 4, marginBottom: 12 }} />
          <label style={{ fontSize: 12, fontWeight: 600, color: T.dim }}>Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" style={{ ...inputStyle, marginTop: 4, marginBottom: 20 }} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
          <button onClick={handleSubmit} disabled={loading} style={{ ...btnPrimary, width: "100%", padding: "14px 0", fontSize: 16, opacity: loading ? 0.6 : 1 }}>
            {loading ? "..." : mode === "login" ? "Log In" : "Create Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Drop Zone ───
function DropZone({ onUpload, loading }) {
  const [drag, setDrag] = useState(false); const ref = useRef(null);
  const handleFile = useCallback(file => {
    if (!file?.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => onUpload(reader.result.split(",")[1], file.type, reader.result);
    reader.readAsDataURL(file);
  }, [onUpload]);
  return (
    <div onClick={() => !loading && ref.current?.click()} onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
      style={{ border: "2px dashed " + (drag ? T.accent : T.border), borderRadius: 16, padding: "40px 20px", textAlign: "center", cursor: loading ? "wait" : "pointer", background: drag ? T.accentDim : T.surface, transition: "all 0.3s" }}>
      <input ref={ref} type="file" accept="image/*" onChange={e => handleFile(e.target.files[0])} style={{ display: "none" }} />
      {loading ? (
        <div><div className="spinner" /><p style={{ color: T.accent, marginTop: 14, fontSize: 14, fontWeight: 600 }}>Reading your bet slip...</p></div>
      ) : (
        <><div style={{ fontSize: 36, marginBottom: 8 }}>📸</div><p style={{ color: T.text, fontWeight: 700, fontSize: 16, margin: 0 }}>Drop your bet slip here</p><p style={{ color: T.dim, fontSize: 13, marginTop: 4 }}>Screenshot from any sportsbook</p></>
      )}
    </div>
  );
}

// ─── Leg Card ───
function LegCard({ leg, i }) {
  const [open, setOpen] = useState(false);
  const col = confColor(leg.confidence);
  const vColors = { Smart: T.accent, Lean: T.warn, Risky: T.danger, Fade: T.danger };
  const vCol = vColors[leg.verdict] || T.dim;
  return (
    <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <div style={{ width: 26, height: 26, borderRadius: 7, background: col + "22", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12, color: col, flexShrink: 0 }}>{i + 1}</div>
            <span style={{ fontWeight: 600, color: T.text, fontSize: 13 }}>{leg.selection}</span>
          </div>
          <span style={{ background: col + "22", color: col, padding: "3px 8px", borderRadius: 6, fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{leg.confidence}%</span>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
          {leg.verdict && <span style={{ background: vCol + "18", color: vCol, padding: "3px 8px", borderRadius: 5, fontWeight: 700, fontSize: 11 }}>{leg.verdict}</span>}
          {leg.historicalHitRate && <span style={{ background: T.purpleDim, color: T.purple, padding: "3px 8px", borderRadius: 5, fontWeight: 600, fontSize: 11 }}>{leg.historicalHitRate}</span>}
        </div>
        {leg.matchupAnalysis && (
          <div style={{ fontSize: 12, color: T.dim, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: open ? 99 : 2, WebkitBoxOrient: "vertical" }}>
            <span style={{ color: T.blue, fontWeight: 600 }}>Matchup: </span>{leg.matchupAnalysis}
          </div>
        )}
        <button onClick={() => setOpen(!open)} style={{ background: "none", border: "1px solid " + T.border, borderRadius: 8, padding: "5px 12px", color: T.dim, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", width: "100%", marginTop: 8 }}>
          {open ? "Hide ▲" : "Full Analysis ▼"}
        </button>
      </div>
      {open && (
        <div style={{ padding: "0 16px 14px", borderTop: "1px solid " + T.border }}>
          <div style={{ paddingTop: 12 }}>
            <p style={{ color: T.text, fontSize: 12, lineHeight: 1.6, margin: "0 0 10px" }}>{leg.reasoning}</p>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.dim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Key Stats</div>
            {leg.keyStats?.map((s, j) => (
              <div key={j} style={{ display: "flex", gap: 6, marginBottom: 4, fontSize: 12, color: T.text, lineHeight: 1.5 }}>
                <span style={{ color: T.accent, flexShrink: 0 }}>●</span><span>{s}</span>
              </div>
            ))}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
              {leg.recentForm && <div style={{ background: T.blueDim, border: "1px solid " + T.blue + "33", borderRadius: 8, padding: "6px 10px", flex: "1 1 120px" }}><div style={{ fontSize: 9, color: T.dim, fontWeight: 600 }}>FORM</div><div style={{ fontSize: 12, fontWeight: 700, color: T.blue, marginTop: 2 }}>{leg.recentForm}</div></div>}
              {leg.injuries && leg.injuries !== "None reported" && <div style={{ background: T.dangerDim, border: "1px solid " + T.danger + "33", borderRadius: 8, padding: "6px 10px", flex: "1 1 120px" }}><div style={{ fontSize: 9, color: T.dim, fontWeight: 600 }}>INJURIES</div><div style={{ fontSize: 12, fontWeight: 700, color: T.danger, marginTop: 2 }}>{leg.injuries}</div></div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bet Slip Card ───
function BetSlipCard({ bet, onClick }) {
  const col = bet.status === "won" ? T.accent : bet.status === "lost" ? T.danger : bet.status === "active" ? T.blue : T.dim;
  return (
    <div onClick={onClick} style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, padding: "14px 16px", cursor: "pointer", transition: "all 0.2s" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ background: col + "22", color: col, padding: "2px 7px", borderRadius: 5, fontWeight: 700, fontSize: 10 }}>{bet.status.toUpperCase()}</span>
            {bet.sportsbook && <span style={{ color: T.dim, fontSize: 11 }}>{bet.sportsbook}</span>}
          </div>
          <div style={{ fontWeight: 600, color: T.text, fontSize: 13, marginBottom: 3 }}>{bet.ai_verdict || bet.bet_type + " " + bet.total_odds}</div>
          <div style={{ fontSize: 11, color: T.dim }}>{bet.wager && "$" + bet.wager}{bet.potential_payout && " → $" + bet.potential_payout}</div>
        </div>
        {bet.overall_confidence && <ConfRing value={bet.overall_confidence} size={48} />}
      </div>
    </div>
  );
}

// ─── Odds Comparison Card ───
function OddsComparison({ odds, parsedBet }) {
  if (!odds || odds.length === 0) return null;
  const matchedGames = [];
  for (const bet of (parsedBet.bets || [])) {
    const home = bet.teams?.home?.toLowerCase() || "";
    const away = bet.teams?.away?.toLowerCase() || "";
    const match = odds.find(o => {
      const oh = (o.home_team || "").toLowerCase(), oa = (o.away_team || "").toLowerCase();
      return (oh.includes(home) || home.includes(oh) || oa.includes(away) || away.includes(oa)) && (home || away);
    });
    if (match) matchedGames.push({ bet, game: match });
  }
  if (matchedGames.length === 0) return null;
  return (
    <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.warn, letterSpacing: 1, marginBottom: 10 }}>ODDS COMPARISON</div>
      {matchedGames.map(({ bet, game }, i) => (
        <div key={i} style={{ marginBottom: i < matchedGames.length - 1 ? 12 : 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 6 }}>{game.home_team} vs {game.away_team}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {game.bookmakers?.slice(0, 6).map((bk, j) => {
              const market = bk.markets?.find(m => m.key === "h2h");
              if (!market) return null;
              return (
                <div key={j} style={{ background: T.surface, borderRadius: 6, padding: "4px 8px", fontSize: 10, flex: "1 1 80px", minWidth: 80 }}>
                  <div style={{ color: T.dim, fontWeight: 600, marginBottom: 2 }}>{bk.title}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {market.outcomes?.map((o, k) => (
                      <span key={k} style={{ color: T.text, fontWeight: 700, fontSize: 11 }}>{o.name?.substring(0,3)}: {o.price > 0 ? "+" : ""}{o.price}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main App ───
export default function LayStat() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [page, setPage] = useState("dashboard");
  const [bets, setBets] = useState([]); const [bankroll, setBankroll] = useState([]);
  const [uploading, setUploading] = useState(false); const [uploadStep, setUploadStep] = useState("");
  const [currentParsed, setCurrentParsed] = useState(null); const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [currentPreview, setCurrentPreview] = useState(null); const [currentOdds, setCurrentOdds] = useState(null);
  const [selectedBet, setSelectedBet] = useState(null); const [selectedLegs, setSelectedLegs] = useState([]);
  const [showBankroll, setShowBankroll] = useState(false);
  const [txnType, setTxnType] = useState("deposit"); const [txnAmount, setTxnAmount] = useState(""); const [txnBook, setTxnBook] = useState("");
  const [wagerInput, setWagerInput] = useState(""); const [payoutInput, setPayoutInput] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const t = localStorage.getItem("sb_token");
    if (!t) { setAuthLoading(false); return; }
    fetch(SUPABASE_URL + "/auth/v1/user", { headers: { apikey: SUPABASE_KEY, Authorization: "Bearer " + t } })
      .then(r => r.json())
      .then(d => { if (d.id) setUser(d); else { localStorage.removeItem("sb_token"); localStorage.removeItem("sb_refresh"); } })
      .catch(() => { localStorage.removeItem("sb_token"); localStorage.removeItem("sb_refresh"); })
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      supaFetch("/rest/v1/bets?select=*&order=created_at.desc&limit=50").catch(() => []),
      supaFetch("/rest/v1/bankroll_transactions?select=*&order=created_at.desc&limit=50").catch(() => []),
    ]).then(([b, bk]) => { setBets(b || []); setBankroll(bk || []); });
  }, [user]);

  const currentBalance = useMemo(() => bankroll.length ? parseFloat(bankroll[0]?.balance_after || 0) : 0, [bankroll]);
  const todayPL = useMemo(() => {
    const today = new Date().toDateString();
    return bankroll.filter(t => new Date(t.created_at).toDateString() === today).filter(t => ["bet_won","bet_lost","bet_push","cashout"].includes(t.type)).reduce((s, t) => s + parseFloat(t.amount), 0);
  }, [bankroll]);

  const handleAuth = (u) => setUser(u);
  const handleLogout = () => { localStorage.removeItem("sb_token"); localStorage.removeItem("sb_refresh"); setUser(null); setBets([]); setBankroll([]); };

  const handleUpload = useCallback(async (base64, mediaType, preview) => {
    setUploading(true); setUploadStep("parsing"); setCurrentPreview(preview); setPage("analyze");
    setCurrentAnalysis(null); setCurrentParsed(null); setCurrentOdds(null);
    try {
      const parsed = await parseBetSlip(base64, mediaType);
      setCurrentParsed(parsed); setWagerInput(parsed.wager || ""); setPayoutInput(parsed.potentialPayout || "");
      setUploading(false); setUploadStep("analyzing");
      // Fetch odds and analysis in parallel
      const [analysis, odds] = await Promise.all([analyzeDeep(parsed), fetchOddsComparison(parsed).catch(() => [])]);
      setCurrentAnalysis(analysis); setCurrentOdds(odds); setUploadStep("done");
    } catch (err) { console.error(err); setUploadStep("error"); setUploading(false); setErrorMsg(err.message); }
  }, []);

  const saveBet = async () => {
    if (!currentParsed || !currentAnalysis) return;
    try {
      const betRow = await supaFetch("/rest/v1/bets", { method: "POST", body: JSON.stringify({
        user_id: user.id, sportsbook: currentParsed.sportsbook || "Bookie", bet_type: currentParsed.betType || "parlay",
        total_odds: currentParsed.totalOdds, wager: wagerInput ? parseFloat(wagerInput) : null,
        potential_payout: payoutInput ? parseFloat(payoutInput) : null, status: "pending",
        overall_confidence: currentAnalysis.overallConfidence, ai_verdict: currentAnalysis.overallVerdict,
        ai_recommendations: currentAnalysis.recommendations || [],
      })});
      if (betRow?.[0]) {
        const betId = betRow[0].id;
        const legs = currentParsed.bets.map((b, i) => {
          const a = currentAnalysis.legs?.[i] || {};
          return { bet_id: betId, sport: b.sport, league: b.league, bet_type: b.betType, home_team: b.teams?.home, away_team: b.teams?.away, selection: b.selection, odds: b.odds, line: b.line ? parseFloat(b.line) : null, player_name: b.player, stat_type: b.statType, status: "pending", confidence: a.confidence, verdict: a.verdict, reasoning: a.reasoning, key_stats: a.keyStats || [], recent_form: a.recentForm, historical_hit_rate: a.historicalHitRate, injuries: a.injuries };
        });
        await supaFetch("/rest/v1/bet_legs", { method: "POST", body: JSON.stringify(legs) });
        if (wagerInput) {
          await supaFetch("/rest/v1/bankroll_transactions", { method: "POST", body: JSON.stringify({ user_id: user.id, type: "bet_placed", amount: -parseFloat(wagerInput), balance_after: currentBalance - parseFloat(wagerInput), bet_id: betId, sportsbook: currentParsed.sportsbook }) });
        }
        const [nb, nbk] = await Promise.all([supaFetch("/rest/v1/bets?select=*&order=created_at.desc&limit=50").catch(()=>[]), supaFetch("/rest/v1/bankroll_transactions?select=*&order=created_at.desc&limit=50").catch(()=>[])]);
        setBets(nb||[]); setBankroll(nbk||[]);
      }
      setCurrentParsed(null); setCurrentAnalysis(null); setCurrentPreview(null); setCurrentOdds(null); setUploadStep(""); setWagerInput(""); setPayoutInput(""); setPage("dashboard");
    } catch (err) { console.error("Save error:", err); }
  };

  const addTransaction = async () => {
    if (!txnAmount) return;
    const amt = parseFloat(txnAmount), signed = txnType === "withdrawal" ? -amt : amt;
    try {
      await supaFetch("/rest/v1/bankroll_transactions", { method: "POST", body: JSON.stringify({ user_id: user.id, type: txnType, amount: signed, balance_after: currentBalance + signed, sportsbook: txnBook || null }) });
      const nbk = await supaFetch("/rest/v1/bankroll_transactions?select=*&order=created_at.desc&limit=50").catch(()=>[]);
      setBankroll(nbk||[]); setTxnAmount(""); setTxnBook(""); setShowBankroll(false);
    } catch (err) { console.error(err); }
  };

  const settleBet = async (betId, result) => {
    const bet = bets.find(b => b.id === betId); if (!bet) return;
    const payout = result === "won" ? parseFloat(bet.potential_payout||0) : result === "push" ? parseFloat(bet.wager||0) : 0;
    try {
      await supaFetch("/rest/v1/bets?id=eq." + betId, { method: "PATCH", body: JSON.stringify({ status: result, actual_payout: payout, settled_at: new Date().toISOString() }) });
      if (bet.wager) await supaFetch("/rest/v1/bankroll_transactions", { method: "POST", body: JSON.stringify({ user_id: user.id, type: result === "won" ? "bet_won" : result === "push" ? "bet_push" : "bet_lost", amount: payout, balance_after: currentBalance + payout, bet_id: betId }) });
      const [nb, nbk] = await Promise.all([supaFetch("/rest/v1/bets?select=*&order=created_at.desc&limit=50").catch(()=>[]), supaFetch("/rest/v1/bankroll_transactions?select=*&order=created_at.desc&limit=50").catch(()=>[])]);
      setBets(nb||[]); setBankroll(nbk||[]); setSelectedBet(null); setPage("dashboard");
    } catch (err) { console.error(err); }
  };

  const viewBet = async (bet) => {
    setSelectedBet(bet);
    try { const legs = await supaFetch("/rest/v1/bet_legs?bet_id=eq." + bet.id + "&select=*"); setSelectedLegs(legs||[]); } catch { setSelectedLegs([]); }
    setPage("detail");
  };

  // Bankroll by sportsbook
  const bookBalances = useMemo(() => {
    const bk2 = {};
    [...bankroll].reverse().forEach(tx => {
      const name = tx.sportsbook || "Other";
      if (!bk2[name]) bk2[name] = 0;
      bk2[name] += parseFloat(tx.amount);
    });
    return bk2;
  }, [bankroll]);

  if (authLoading) return <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}><div className="spinner" /></div>;
  if (!user) return <AuthScreen onAuth={handleAuth} />;

  const activeBets = bets.filter(b => b.status === "pending" || b.status === "active");
  const settledBets = bets.filter(b => b.status === "won" || b.status === "lost" || b.status === "push");
  const totalWagered = bets.reduce((s, b) => s + parseFloat(b.wager || 0), 0);
  const totalWon = bets.filter(b => b.status === "won").reduce((s, b) => s + parseFloat(b.actual_payout || 0), 0);
  const record = { w: bets.filter(b => b.status === "won").length, l: bets.filter(b => b.status === "lost").length };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Outfit', 'DM Sans', sans-serif", color: T.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&family=Bebas+Neue&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${T.bg}; }
        .spinner { width: 40px; height: 40px; border: 3px solid ${T.border}; border-top: 3px solid ${T.accent}; border-radius: 50%; margin: 0 auto; animation: spin 0.8s linear infinite; }
        input:focus { border-color: ${T.accent} !important; }
      `}</style>

      <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", borderBottom: "1px solid " + T.border, background: T.surface, position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => { setPage("dashboard"); setSelectedBet(null); }}>
          <Logo size={32} />
          <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: 1, fontFamily: "'Bebas Neue', sans-serif" }}>LAYSTAT</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: T.dim }}>{user.email?.split("@")[0]}</span>
          <button onClick={handleLogout} style={{ ...btnSecondary, padding: "5px 8px" }}><Icon d={Icons.logout} size={14} color={T.dim} /></button>
        </div>
      </nav>

      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: T.surface, borderTop: "1px solid " + T.border, display: "flex", zIndex: 100, padding: "4px 0 env(safe-area-inset-bottom, 4px)" }}>
        {[{ id: "dashboard", icon: Icons.home, label: "Home" }, { id: "upload", icon: Icons.upload, label: "Analyze" }, { id: "bankrolls", icon: Icons.dollar, label: "Bankroll" }, { id: "history", icon: Icons.history, label: "History" }].map(tab => (
          <button key={tab.id} onClick={() => { setPage(tab.id); setSelectedBet(null); }}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "6px 0", background: "none", border: "none", cursor: "pointer" }}>
            <Icon d={tab.icon} size={18} color={page === tab.id ? T.accent : T.dim} />
            <span style={{ fontSize: 9, fontWeight: 600, color: page === tab.id ? T.accent : T.dim }}>{tab.label}</span>
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "16px 12px 90px", animation: "fadeUp 0.4s ease" }}>

        {/* DASHBOARD */}
        {page === "dashboard" && (<>
          <div style={{ background: "linear-gradient(135deg, " + T.card + ", " + T.surface + ")", border: "1px solid " + T.border, borderRadius: 14, padding: "18px 20px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><div style={{ fontSize: 10, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 3 }}>BANKROLL</div><div style={{ fontSize: 26, fontWeight: 800, color: T.text, fontFamily: "'JetBrains Mono'" }}>${currentBalance.toFixed(2)}</div></div>
              <div style={{ textAlign: "right" }}><div style={{ fontSize: 10, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 3 }}>TODAY</div><div style={{ fontSize: 18, fontWeight: 800, color: todayPL >= 0 ? T.accent : T.danger, fontFamily: "'JetBrains Mono'" }}>{todayPL >= 0 ? "+" : ""}${todayPL.toFixed(2)}</div></div>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
              {[{ l: "RECORD", v: record.w + "W " + record.l + "L", c: T.accent, b: T.accentDim }, { l: "WAGERED", v: "$" + totalWagered.toFixed(0), c: T.blue, b: T.blueDim }, { l: "PROFIT", v: "$" + (totalWon - totalWagered).toFixed(0), c: totalWon - totalWagered >= 0 ? T.accent : T.danger, b: totalWon - totalWagered >= 0 ? T.accentDim : T.dangerDim }].map(s => (
                <div key={s.l} style={{ flex: 1, background: s.b, borderRadius: 8, padding: "6px 8px", textAlign: "center" }}><div style={{ fontSize: 9, color: T.dim, fontWeight: 600 }}>{s.l}</div><div style={{ fontSize: 13, fontWeight: 700, color: s.c, fontFamily: "'JetBrains Mono'" }}>{s.v}</div></div>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 8 }}>ACTIVE BETS ({activeBets.length})</div>
          {activeBets.length === 0 ? (
            <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, padding: "28px 20px", textAlign: "center", marginBottom: 16 }}>
              <p style={{ color: T.dim, fontSize: 13 }}>No active bets</p>
              <button onClick={() => setPage("upload")} style={{ ...btnPrimary, marginTop: 10, padding: "8px 18px", fontSize: 12 }}>Analyze a Bet</button>
            </div>
          ) : <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>{activeBets.map(b => <BetSlipCard key={b.id} bet={b} onClick={() => viewBet(b)} />)}</div>}
          {settledBets.length > 0 && (<>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 8 }}>RECENT RESULTS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{settledBets.slice(0, 5).map(b => <BetSlipCard key={b.id} bet={b} onClick={() => viewBet(b)} />)}</div>
          </>)}
        </>)}

        {/* UPLOAD / ANALYZE */}
        {(page === "upload" || page === "analyze") && (<>
          {!currentParsed && !uploading && uploadStep !== "error" && (<>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Analyze a Bet</h2>
            <p style={{ color: T.dim, fontSize: 13, marginBottom: 16 }}>Upload a screenshot of your bet slip.</p>
            <DropZone onUpload={handleUpload} loading={false} />
          </>)}
          {uploading && <div style={{ textAlign: "center", padding: "50px 20px" }}><div className="spinner" /><p style={{ color: T.accent, marginTop: 16, fontSize: 15, fontWeight: 700 }}>Reading your bet slip...</p></div>}
          {uploadStep === "error" && <div style={{ textAlign: "center", padding: "40px 20px" }}><p style={{ color: T.danger, fontWeight: 600, marginBottom: 6 }}>Could not parse that bet slip</p>{errorMsg && <p style={{ color: T.dim, fontSize: 11, marginBottom: 12, wordBreak: "break-all" }}>{errorMsg.substring(0, 200)}</p>}<button onClick={() => { setUploadStep(""); setUploading(false); setCurrentParsed(null); setCurrentAnalysis(null); setErrorMsg(""); }} style={btnPrimary}>Try Again</button></div>}
          {currentParsed && (
            <div style={{ animation: "fadeUp 0.4s ease" }}>
              <div style={{ background: "linear-gradient(135deg, " + T.card + ", " + T.surface + ")", border: "1px solid " + T.border, borderRadius: 16, padding: 20, marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
                {currentPreview && <img src={currentPreview} alt="slip" style={{ width: 60, height: 80, objectFit: "cover", borderRadius: 10, border: "1px solid " + T.border }} />}
                {currentAnalysis ? <ConfRing value={currentAnalysis.overallConfidence} size={80} /> : <div style={{ width: 60, height: 60, borderRadius: "50%", border: "3px solid " + T.border, borderTop: "3px solid " + T.accent, animation: "spin 1.5s linear infinite" }} />}
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 3 }}>{currentParsed.sportsbook || "Sportsbook"} — {currentParsed.bets?.length || 0} LEGS</div>
                  {currentAnalysis ? (<><div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.4, marginBottom: 6 }}>{currentAnalysis.overallVerdict}</div>
                    {currentAnalysis.edgeRating && <span style={{ background: currentAnalysis.edgeRating === "Positive EV" ? T.accentDim : currentAnalysis.edgeRating === "Negative EV" ? T.dangerDim : T.blueDim, color: currentAnalysis.edgeRating === "Positive EV" ? T.accent : currentAnalysis.edgeRating === "Negative EV" ? T.danger : T.blue, padding: "2px 8px", borderRadius: 5, fontWeight: 700, fontSize: 11 }}>{currentAnalysis.edgeRating}</span>}</>
                  ) : <div style={{ fontSize: 14, fontWeight: 700, color: T.accent }}>Pulling deep stats...<br /><span style={{ fontSize: 12, color: T.dim, fontWeight: 500 }}>Searching injuries, matchups, trends</span></div>}
                </div>
              </div>
              <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, padding: "14px 16px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.dim, marginBottom: 8 }}>WAGER DETAILS</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 10, color: T.dim, fontWeight: 600 }}>Wager ($)</label><input value={wagerInput} onChange={e => setWagerInput(e.target.value)} placeholder="0.00" type="number" style={{ ...inputStyle, marginTop: 3 }} /></div>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 10, color: T.dim, fontWeight: 600 }}>Payout ($)</label><input value={payoutInput} onChange={e => setPayoutInput(e.target.value)} placeholder="0.00" type="number" style={{ ...inputStyle, marginTop: 3 }} /></div>
                </div>
              </div>
              {/* Odds Comparison */}
              {currentOdds && <OddsComparison odds={currentOdds} parsedBet={currentParsed} />}
              <div style={{ fontSize: 11, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 8 }}>LEG BREAKDOWN {!currentAnalysis && <span style={{ color: T.accent, animation: "pulse 1.5s infinite" }}>— analyzing...</span>}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                {currentAnalysis ? currentAnalysis.legs?.map((leg, i) => <LegCard key={i} leg={leg} i={i} />) :
                  currentParsed.bets?.map((bet, i) => (
                    <div key={i} style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: T.faint, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 11, color: T.dim }}>{i+1}</div>
                      <div style={{ flex: 1 }}><div style={{ fontWeight: 600, color: T.text, fontSize: 13 }}>{bet.selection}</div><div style={{ fontSize: 11, color: T.dim, marginTop: 1 }}>{bet.sport} — {bet.betType}{bet.odds && " — " + bet.odds}</div></div>
                      <div style={{ width: 20, height: 20, border: "2px solid " + T.border, borderTop: "2px solid " + T.accent, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                    </div>
                  ))}
              </div>
              {currentAnalysis?.recommendations?.length > 0 && (
                <div style={{ background: T.blueDim, border: "1px solid " + T.blue + "33", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.blue, marginBottom: 8 }}>RECOMMENDATIONS</div>
                  {currentAnalysis.recommendations.map((r, i) => <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4, fontSize: 12, color: T.text, lineHeight: 1.5 }}><span style={{ color: T.blue }}>→</span><span>{r}</span></div>)}
                </div>
              )}
              <button onClick={saveBet} disabled={!currentAnalysis} style={{ ...btnPrimary, width: "100%", padding: "14px 0", fontSize: 15, opacity: currentAnalysis ? 1 : 0.5, cursor: currentAnalysis ? "pointer" : "wait" }}>{currentAnalysis ? "Save Bet & Track" : "Waiting for analysis..."}</button>
              <button onClick={() => { setCurrentParsed(null); setCurrentAnalysis(null); setCurrentOdds(null); setUploadStep(""); setPage("upload"); }} style={{ ...btnSecondary, width: "100%", marginTop: 6, padding: "10px 0" }}>Discard</button>
            </div>
          )}
        </>)}

        {/* BET DETAIL */}
        {page === "detail" && selectedBet && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <button onClick={() => { setPage("dashboard"); setSelectedBet(null); }} style={{ ...btnSecondary, marginBottom: 14, padding: "6px 12px", fontSize: 12 }}>← Back</button>
            <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, padding: 18, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <div>
                  {(() => { const c = selectedBet.status === "won" ? T.accent : selectedBet.status === "lost" ? T.danger : T.blue;
                    return <span style={{ background: c+"22", color: c, padding: "2px 8px", borderRadius: 5, fontWeight: 700, fontSize: 11, marginRight: 6 }}>{selectedBet.status.toUpperCase()}</span>; })()}
                  <span style={{ color: T.dim, fontSize: 11 }}>{selectedBet.sportsbook}</span>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6 }}>{selectedBet.ai_verdict}</div>
                  <div style={{ fontSize: 12, color: T.dim, marginTop: 2 }}>{selectedBet.total_odds && "Odds: " + selectedBet.total_odds}{selectedBet.wager && " — $" + selectedBet.wager}{selectedBet.potential_payout && " → $" + selectedBet.potential_payout}</div>
                </div>
                {selectedBet.overall_confidence && <ConfRing value={selectedBet.overall_confidence} size={70} />}
              </div>
              {(selectedBet.status === "pending" || selectedBet.status === "active") && (
                <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
                  <button onClick={() => settleBet(selectedBet.id, "won")} style={{ ...btnBase, flex: 1, background: T.accentDim, color: T.accent, padding: "8px 0", border: "1px solid " + T.accent + "44" }}>Won ✓</button>
                  <button onClick={() => settleBet(selectedBet.id, "lost")} style={{ ...btnBase, flex: 1, background: T.dangerDim, color: T.danger, padding: "8px 0", border: "1px solid " + T.danger + "44" }}>Lost ✗</button>
                  <button onClick={() => settleBet(selectedBet.id, "push")} style={{ ...btnBase, flex: 1, background: T.faint, color: T.dim, padding: "8px 0", border: "1px solid " + T.border }}>Push</button>
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 8 }}>LEGS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{selectedLegs.map((leg, i) => <LegCard key={leg.id} i={i} leg={{ selection: leg.selection, confidence: leg.confidence, verdict: leg.verdict, reasoning: leg.reasoning, keyStats: leg.key_stats, recentForm: leg.recent_form, historicalHitRate: leg.historical_hit_rate, injuries: leg.injuries }} />)}</div>
            {selectedBet.ai_recommendations?.length > 0 && (
              <div style={{ background: T.blueDim, border: "1px solid " + T.blue + "33", borderRadius: 14, padding: "14px 16px", marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.blue, marginBottom: 8 }}>RECOMMENDATIONS</div>
                {selectedBet.ai_recommendations.map((r, i) => <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4, fontSize: 12, color: T.text, lineHeight: 1.5 }}><span style={{ color: T.blue }}>→</span><span>{r}</span></div>)}
              </div>
            )}
          </div>
        )}

        {/* BANKROLL */}
        {page === "bankrolls" && (<>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800 }}>Bankroll</h2>
            <button onClick={() => setShowBankroll(true)} style={{ ...btnPrimary, padding: "8px 14px", fontSize: 12 }}>+ Add Funds</button>
          </div>
          <div style={{ background: "linear-gradient(135deg, " + T.card + ", " + T.surface + ")", border: "1px solid " + T.border, borderRadius: 14, padding: "20px 18px", marginBottom: 14, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 4 }}>TOTAL BALANCE</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: T.text, fontFamily: "'JetBrains Mono'" }}>${currentBalance.toFixed(2)}</div>
          </div>
          {/* By sportsbook */}
          {Object.keys(bookBalances).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {Object.entries(bookBalances).map(([book, bal]) => (
                <div key={book} style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 10, padding: "8px 12px", flex: "1 1 120px", minWidth: 120 }}>
                  <div style={{ fontSize: 10, color: T.dim, fontWeight: 600 }}>{book}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: bal >= 0 ? T.accent : T.danger, fontFamily: "'JetBrains Mono'", marginTop: 2 }}>${bal.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 8 }}>TRANSACTIONS</div>
          {bankroll.length === 0 ? <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, padding: "24px 18px", textAlign: "center" }}><p style={{ color: T.dim, fontSize: 13 }}>No transactions yet</p></div> :
            bankroll.map(tx => (
              <div key={tx.id} style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 10, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><div style={{ fontWeight: 600, fontSize: 12, color: T.text, textTransform: "capitalize" }}>{tx.type.replace(/_/g, " ")}</div><div style={{ fontSize: 10, color: T.dim, marginTop: 1 }}>{new Date(tx.created_at).toLocaleDateString()}{tx.sportsbook && " — " + tx.sportsbook}</div></div>
                <div style={{ fontWeight: 700, fontSize: 13, fontFamily: "'JetBrains Mono'", color: parseFloat(tx.amount) >= 0 ? T.accent : T.danger }}>{parseFloat(tx.amount) >= 0 ? "+" : ""}${parseFloat(tx.amount).toFixed(2)}</div>
              </div>
            ))}
          {showBankroll && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }} onClick={e => e.target === e.currentTarget && setShowBankroll(false)}>
              <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 16, padding: 24, width: "100%", maxWidth: 380, animation: "fadeUp 0.3s ease" }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Add Transaction</h3>
                <div style={{ display: "flex", gap: 4, background: T.surface, borderRadius: 8, padding: 3, marginBottom: 14 }}>
                  {["deposit", "withdrawal"].map(t => <button key={t} onClick={() => setTxnType(t)} style={{ flex: 1, padding: "8px 0", borderRadius: 6, border: "none", cursor: "pointer", background: txnType === t ? T.accent : "transparent", color: txnType === t ? T.bg : T.dim, fontWeight: 700, fontSize: 12, fontFamily: "inherit", textTransform: "capitalize" }}>{t}</button>)}
                </div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.dim }}>Amount ($)</label>
                <input value={txnAmount} onChange={e => setTxnAmount(e.target.value)} placeholder="0.00" type="number" style={{ ...inputStyle, marginTop: 3, marginBottom: 10 }} />
                <label style={{ fontSize: 11, fontWeight: 600, color: T.dim }}>Sportsbook</label>
                <input value={txnBook} onChange={e => setTxnBook(e.target.value)} placeholder="DraftKings, FanDuel, etc." style={{ ...inputStyle, marginTop: 3, marginBottom: 16 }} />
                <button onClick={addTransaction} style={{ ...btnPrimary, width: "100%", padding: "10px 0" }}>Add {txnType}</button>
              </div>
            </div>
          )}
        </>)}

        {/* HISTORY */}
        {page === "history" && (<>
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 14 }}>Bet History</h2>
          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            {[{ l: "WINS", v: record.w, c: T.accent, b: T.accentDim }, { l: "LOSSES", v: record.l, c: T.danger, b: T.dangerDim }, { l: "WIN RATE", v: (record.w + record.l > 0 ? Math.round(record.w / (record.w + record.l) * 100) : 0) + "%", c: T.blue, b: T.blueDim }].map(s => (
              <div key={s.l} style={{ flex: "1 1 90px", background: s.b, border: "1px solid " + s.c + "33", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: T.dim, fontWeight: 600 }}>{s.l}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.c, fontFamily: "'JetBrains Mono'" }}>{s.v}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.dim, letterSpacing: 1, marginBottom: 8 }}>ALL BETS</div>
          {bets.length === 0 ? <div style={{ background: T.card, border: "1px solid " + T.border, borderRadius: 14, padding: "24px 18px", textAlign: "center" }}><p style={{ color: T.dim, fontSize: 13 }}>No bets yet</p></div> :
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{bets.map(b => <BetSlipCard key={b.id} bet={b} onClick={() => viewBet(b)} />)}</div>}
        </>)}
      </div>
    </div>
  );
}